import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Chip,
  LinearProgress,
  Button,
  Collapse,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

// What a teacher can act on, with the sample behind every number:
//  * mastery per level (template) — levels are never averaged together;
//  * who is due for a reassessment;
//  * who has not been assessed yet (kept out of the averages instead of "0%").
// All numbers come from /api/analytics/teacher (hidden assessments excluded there).

const masteryColor = (pct) =>
  pct >= 70 ? 'success' : pct >= 40 ? 'warning' : 'error';

function LevelCard({ name, stats, active, onPick, en }) {
  const total = stats.studentCount;
  const scored = stats.scoredCount ?? total;
  return (
    <Paper
      variant="outlined"
      onClick={onPick}
      sx={{
        p: 2.5,
        borderRadius: 3,
        height: '100%',
        cursor: onPick ? 'pointer' : 'default',
        borderColor: active ? 'primary.main' : 'divider',
        borderWidth: active ? 2 : 1,
      }}
    >
      <Box
        sx={{ display: 'flex', justifyContent: 'space-between', mb: 1, gap: 1 }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          {name}
        </Typography>
        {scored > 0 ? (
          <Chip
            size="small"
            color={masteryColor(stats.avgMastery)}
            label={`${stats.avgMastery}%`}
            sx={{ fontWeight: 700 }}
          />
        ) : (
          <Chip
            size="small"
            variant="outlined"
            label={en ? 'not assessed' : 'нет оценок'}
          />
        )}
      </Box>
      <LinearProgress
        variant="determinate"
        value={scored > 0 ? stats.avgMastery : 0}
        color={masteryColor(stats.avgMastery)}
        sx={{ height: 8, borderRadius: 4, mb: 1.25 }}
      />
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: 'block' }}
      >
        {en
          ? `Based on ${scored} of ${total} students`
          : `По ${scored} из ${total} учеников`}
        {stats.partialCount > 0
          ? en
            ? ` · ${stats.partialCount} partial (some units only)`
            : ` · ${stats.partialCount} частичных (не все темы)`
          : ''}
      </Typography>
      {(stats.trendUp > 0 || stats.trendDown > 0) && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block' }}
        >
          {en ? 'Since previous assessment: ' : 'С прошлой оценки: '}
          <Box component="span" sx={{ color: 'success.main', fontWeight: 700 }}>
            ↑ {stats.trendUp}
          </Box>{' '}
          <Box component="span" sx={{ color: 'error.main', fontWeight: 700 }}>
            ↓ {stats.trendDown}
          </Box>{' '}
          <Box component="span">→ {stats.trendStable ?? 0}</Box>
        </Typography>
      )}
    </Paper>
  );
}

export default function ClassOverview({
  data,
  en,
  activeTemplate,
  onPickTemplate,
}) {
  const [showNotAssessed, setShowNotAssessed] = useState(false);
  const levels = Object.entries(data.templateAverages || {});
  const queue = data.reassessQueue || [];
  const notAssessed = data.notAssessed || [];
  const afterDays = data.meta?.reassessAfterDays ?? 30;

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="h6" sx={{ fontWeight: 800, mt: 4, mb: 0.5 }}>
        {en ? 'Mastery by level' : 'Освоение по уровням'}
      </Typography>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ mb: 2, maxWidth: 780 }}
      >
        {en
          ? 'Each level is measured on its own words. Students who have not been assessed yet are not counted as 0%.'
          : 'Каждый уровень считается по своим словам. Ученики без оценок не засчитываются как 0%.'}
      </Typography>
      {levels.length === 0 ? (
        <Typography color="text.secondary">
          {en ? 'No assessments yet.' : 'Оценок пока нет.'}
        </Typography>
      ) : (
        <Grid container spacing={2}>
          {levels.map(([name, stats]) => (
            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={name}>
              <LevelCard
                name={name}
                stats={stats}
                en={en}
                active={activeTemplate === name}
                onPick={
                  onPickTemplate
                    ? () =>
                        onPickTemplate(activeTemplate === name ? null : name)
                    : undefined
                }
              />
            </Grid>
          ))}
        </Grid>
      )}

      <Typography variant="h6" sx={{ fontWeight: 800, mt: 5, mb: 0.5 }}>
        {en ? 'Due for reassessment' : 'Пора переоценить'}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {en
          ? `Last assessed ${afterDays}+ days ago (counted from the last edit of the assessment).`
          : `Последняя оценка ${afterDays}+ дней назад (считаем от последнего изменения оценки).`}
      </Typography>
      {queue.length === 0 ? (
        <Typography color="text.secondary">
          {en
            ? 'Everyone assessed is up to date.'
            : 'У всех оценённых учеников оценка свежая.'}
        </Typography>
      ) : (
        <Paper variant="outlined" sx={{ borderRadius: 3, overflow: 'hidden' }}>
          {queue.slice(0, 10).map((q, i) => (
            <Box
              key={String(q.studentId)}
              component={RouterLink}
              to={`/student/${q.studentId}`}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                px: 2,
                py: 1.25,
                color: 'inherit',
                textDecoration: 'none',
                borderTop: i ? '1px solid' : 'none',
                borderColor: 'divider',
                '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              <Typography sx={{ flex: 1, fontWeight: 600 }} noWrap>
                {q.name}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap>
                {q.templateName} · {q.avgMastery}%
              </Typography>
              <Chip
                size="small"
                color="warning"
                variant="outlined"
                label={
                  en
                    ? `${q.daysSinceAssessed} d ago`
                    : `${q.daysSinceAssessed} дн. назад`
                }
              />
            </Box>
          ))}
          {queue.length > 10 && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', p: 1.5 }}
            >
              {en
                ? `and ${queue.length - 10} more`
                : `и ещё ${queue.length - 10}`}
            </Typography>
          )}
        </Paper>
      )}

      {notAssessed.length > 0 && (
        <Box sx={{ mt: 3 }}>
          <Button
            size="small"
            onClick={() => setShowNotAssessed((v) => !v)}
            sx={{ textTransform: 'none', fontWeight: 700 }}
          >
            {en
              ? `${notAssessed.length} students not assessed yet`
              : `Ещё не оценены: ${notAssessed.length}`}{' '}
            {showNotAssessed ? '▲' : '▼'}
          </Button>
          <Collapse in={showNotAssessed}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
              {notAssessed.map((s) => (
                <Chip
                  key={String(s.studentId)}
                  size="small"
                  variant="outlined"
                  label={s.name}
                  component={RouterLink}
                  to={`/student/${s.studentId}`}
                  clickable
                />
              ))}
            </Box>
          </Collapse>
        </Box>
      )}
    </Box>
  );
}
